'use client';

/**
 * SourcePilotAppHostProvider — supplies the concrete AppHost value that
 * `@sourcepilot/ui` reads via `useAppHost()`. This is the bridge that
 * makes ui consumable without ui ever statically referencing this app's
 * directory structure. Every host app under `frontend/apps/*` ships its
 * own equivalent of this file.
 *
 * Responsibilities here that used to live in ui (and got pulled out as
 * part of cleaning up duck-typed entity-shape access):
 *
 *  - **Workspace fetch.** Used to live in `ui/shell/sidebar/mini-app.tsx`
 *    but the PDM-specific `Workspace.setQuery(QueryNames.pdmTasksWorkspaceWithProducts)`
 *    call has no business in a multi-app ui. Now runs as an effect inside
 *    this provider; the host stores result in `productMiniAppStateAtom`;
 *    `workspaceNameAtom` is a derived atom ui consumes.
 *
 *  - **Current entity-task lookup.** Used to live in
 *    `ui/shell/layout/tab-area.tsx` as
 *    `useNodes<Task>(currentProduct?.current_tasks).orderBy('-created_at')`.
 *    Now derived here from the PDM atoms and exposed to ui as
 *    `currentEntityTaskAtom`.
 *
 *  - **Chat-task creation.** Used to live inline in both `tab-area.tsx` and
 *    `ChatView.tsx`, each doing `new ChatTask({...}).save()` +
 *    `editable.currently_attached_to.set(currentProduct)`. Now a single
 *    `createNewChatTaskForCurrentEntity` callback ui invokes — both call
 *    sites converged on the same code path.
 */

import { useEffect, useRef, type ReactNode } from 'react';
import { useAtomValue, useAtom, atom, useStore } from 'jotai';
import { AppHostProvider, type NavigationContextEntity } from '@sourcepilot/ui/framework/state/AppHostContext';
import { useNodes } from '@sourcepilot/framework/relationship-manager';
import { graphStore } from '@sourcepilot/framework/graph-store';
import { patchStateAtom } from '@sourcepilot/ui/framework/state/session-data';

import { ChatTask } from '@sourcepilot/generated/pdm_tasks/api/chattask';
import { createProjectChat } from './project-chat';
import { ModelRegistry } from '@sourcepilot/generated/registries/model-registry';
import { Task } from '@sourcepilot/generated/tasks/api/task';

import { appConfig } from '@/config/app';
import { getListColumns } from '@/config/list-columns';
import { sidebarApps } from '@/test_data/sidebar-data';

// Side-effect: register RFLP per-type tool-call result cards (e.g. the
// AttributeValue card) into the chat-shell registry at app load.
import '@/config/entity-result-cards';

import { ProductMiniApp } from '@/mini-apps/product-miniapp';
import { ComponentsMiniApp } from '@/mini-apps/components-miniapp';
import { SuppliersMiniApp } from '@/mini-apps/suppliers-miniapp';
import { ChatsMiniApp } from '@/mini-apps/chats-miniapp';
import { ComponentSearchMiniApp } from '@/mini-apps/component-search-miniapp';
import { AdminMiniApp } from '@/mini-apps/admin-miniapp';

import { SpawnedTaskDisplay } from '@/ui/organisms/SpawnedTaskDisplay';

import { currentProductAtom, productMiniAppStateAtom, workspaceRefetchSignalAtom } from '@/state/rflp-session';
import { QueryNames } from '@sourcepilot/generated/query-names';

const miniAppRegistry = {
  Products: ProductMiniApp,
  Components: ComponentsMiniApp,
  Suppliers: SuppliersMiniApp,
  Chats: ChatsMiniApp,
  'Component Search': ComponentSearchMiniApp,
  Admin: AdminMiniApp,
};

// ───────────────────────────────────────────────────────────────────────────
// Derived atoms — exposed to ui via AppHostContext.
// ───────────────────────────────────────────────────────────────────────────

/**
 * Reshape the PDM Product atom into the structural NavigationContextEntity
 * contract ui expects. Returns null when there's no product (e.g. on routes
 * outside the product context).
 */
const currentEntityAtom = atom<NavigationContextEntity | null>((get) => {
  const product = get(currentProductAtom);
  if (!product?.id || !product?.name) return null;
  return { id: product.id, name: product.name };
});

/**
 * Workspace name surfaced from the product-mini-app state (which the
 * fetch effect below populates). Drives ui's mini-app title bar.
 */
const workspaceNameAtom = atom<string | null>((get) => {
  return get(productMiniAppStateAtom)?.workspace?.name ?? null;
});

// ───────────────────────────────────────────────────────────────────────────
// Provider
// ───────────────────────────────────────────────────────────────────────────

export function SourcePilotAppHostProvider({ children }: { children: ReactNode }) {
  const jotaiStore = useStore();
  const [productMiniAppState, setProductMiniAppState] = useAtom(productMiniAppStateAtom);
  const patchState = useAtomValue(patchStateAtom);
  const workspaceRefetchSignal = useAtomValue(workspaceRefetchSignalAtom);

  // currentEntityTaskAtom can't be a pure derived `atom(get => …)` because
  // it has to call the React-flavoured `useNodes` hook. Wire it as a value
  // here, then expose via a small read-only atom that mirrors the current
  // useNodes result so ui's `useAtomValue` reads it without subscribing
  // directly to react-only state.
  const currentProduct = useAtomValue(currentProductAtom);
  const entityTasks = useNodes<Task>(currentProduct?.current_tasks).orderBy('-created_at').value();
  const entityTaskRaw = entityTasks?.[0] ?? null;

  // Project the entity task into the {id} contract — ui doesn't need the
  // full Task instance.
  const currentEntityTaskAtomRef = useRef(atom<{ id: string } | null>(null));
  useEffect(() => {
    jotaiStore.set(
      currentEntityTaskAtomRef.current,
      entityTaskRaw?.id ? { id: entityTaskRaw.id } : null,
    );
  }, [entityTaskRaw, jotaiStore]);

  // ─────────────────────────────────────────────────────────────────────────
  // Workspace fetch (moved out of ui/shell/sidebar/mini-app.tsx).
  // PDM-specific query that loads the workspace's products + RFLP projects
  // and writes them to productMiniAppStateAtom (where the mini-app
  // components themselves read from). Re-runs on relevant graph-store
  // events and on patch-state changes.
  // ─────────────────────────────────────────────────────────────────────────

  const isFetchingRef = useRef(false);

  const fetchWorkspace = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    try {
      // Read the FLAT neograph response directly instead of going through the
      // ORM relationship (`workspace.products` / `.rflp_projects` → `.nodes`).
      // Those traverse the graph store's relationship index, which an opened
      // project island clobbers down to just that project: DAG-sourced edges
      // are sticky, and `addRelationship` refuses to let a later query
      // overwrite them — so re-running this query can never restore the full
      // list there. The flat `nodes` array is immune: it's the authoritative
      // set the query returned, filtered by `_label`. (`params: {}` — the
      // workspace is auto-resolved server-side via ServerParam.)
      const res = await fetch(`${process.env.NEXT_PUBLIC_SOURCEPILOT_URL}/api/query`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          query_name: QueryNames.pdmTasksWorkspaceWithProducts,
          params: {},
          format: 'neograph',
        }),
      });
      if (!res.ok) throw new Error(`workspace query failed: ${res.status}`);
      const data = await res.json();
      const ws = (data.root_nodes || [])[0];
      if (ws) {
        const allNodes: any[] = data.nodes || [];
        const products = allNodes.filter((n) => n._label === 'PDMProduct' && n.id && n.name);
        // RFLP projects load alongside products; surfaced in the same
        // sidebar so users can see them without a dedicated miniapp.
        const rflpProjects = allNodes.filter((n) => n._label === 'Project' && n.id && n.name);

        setProductMiniAppState((prev: any) => ({
          ...prev,
          expandedItems: [
            ...prev.expandedItems,
            ...products
              .filter((p: any) => !prev.expandedItems.includes(p.id || ''))
              .map((p: any) => p.id || ''),
          ],
          workspace: {
            id: ws.id || '',
            name: ws.name || '',
            products: products.map((p: any) => ({
              id: p.id || '',
              name: p.name || '',
              created_at: p.created_at ?? null,
            })),
            rflp_projects: rflpProjects.map((p: any) => ({
              id: p.id || '',
              name: p.name || '',
              created_at: p.created_at ?? null,
            })),
          },
        }));
      }
    } catch (error) {
      console.error('Error fetching workspace:', error);
    } finally {
      isFetchingRef.current = false;
    }
  };

  // Initial fetch + subscribe to PDM-relevant graphStore node-created events.
  useEffect(() => {
    fetchWorkspace();
    const unsubscribe = graphStore.onNodeCreated((_node, nodeType) => {
      // Refresh on PDMProduct (the workspace's products) or RFLP Project
      // (the workspace's rflp_projects — `Project` is the Neo4j label, see
      // sourcepilot/models/rflp/project.py).
      if (nodeType === 'PDMProduct' || nodeType === 'Project') {
        fetchWorkspace();
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh on patch-state changes that affect the workspace.
  useEffect(() => {
    if (patchState._has_sketch) {
      fetchWorkspace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patchState]);

  // Authoritative refetch on demand (e.g. after a project delete). Skips the
  // initial 0 so it doesn't double-fetch alongside the mount effect above.
  useEffect(() => {
    if (workspaceRefetchSignal > 0) {
      fetchWorkspace();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceRefetchSignal]);

  // ─────────────────────────────────────────────────────────────────────────
  // createNewChatTaskForCurrentEntity (moved out of ChatView and tab-area).
  // ─────────────────────────────────────────────────────────────────────────

  const createNewChatTaskForCurrentEntity = async (options?: { taskClassName?: string }) => {
    const product = jotaiStore.get(currentProductAtom);
    if (!product?.id) return null;

    const className = options?.taskClassName ?? 'ChatTask';
    const TaskClass = (ModelRegistry[className] as typeof ChatTask | undefined) ?? ChatTask;

    try {
      const task = new TaskClass({ task_name: product.name });
      const savedTask = await task.save();
      if (!savedTask.id) return null;

      // Attach to product via currently_attached_to (two-step pattern —
      // defensive: if the attachment step fails the task is still created).
      try {
        const editable = (savedTask as any).makeEditable();
        editable.currently_attached_to.set(product);
        await editable.save();
      } catch (attachError) {
        console.warn('Failed to attach chat to product:', attachError);
      }

      return { id: savedTask.id };
    } catch (error) {
      console.error('Failed to create chat:', error);
      return null;
    }
  };

  // A new chat that works on a project — for a chat refused a move there,
  // and the new-chat buttons of a project page — created ON the project in
  // one create, so it is bound to it from its first write (one project per
  // chat; Tom, 2026-10-20): never a moment unbound, nor a chat left
  // unattached when a second write fails. Opened in that project's panel.
  const createChatForProject = createProjectChat;

  return (
    <AppHostProvider
      value={{
        shellDefault: appConfig.shellDefault,
        getListColumns,
        miniAppRegistry,
        sidebarItems: sidebarApps,
        SpawnedTaskComponent: SpawnedTaskDisplay,
        currentEntityAtom,
        currentEntityTaskAtom: currentEntityTaskAtomRef.current,
        workspaceNameAtom,
        createNewChatTaskForCurrentEntity,
        createChatForProject,
      }}
    >
      {children}
    </AppHostProvider>
  );
}
